import type { JSX } from 'react';
import {
  Box,
  Chip,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField as MuiTextField,
  Typography,
} from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';

/**
 * Shared input primitives for every builder section editor. Rebuilt on MUI so
 * the whole launcher customizer reads as one design with the rest of the
 * dashboard — the component APIs are unchanged, so the ~13 section editors that
 * use these get MUI inputs without edits.
 */

export interface TextFieldProps {
  label: string;
  hint?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** True for monospace input (IDs, hashes, technical strings). */
  mono?: boolean;
}

export function TextField({ label, hint, value, onChange, placeholder, mono }: TextFieldProps): JSX.Element {
  return (
    <MuiTextField
      label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      size="small"
      fullWidth
      margin="dense"
      {...(hint !== undefined ? { helperText: hint } : {})}
      {...(placeholder !== undefined ? { placeholder } : {})}
      inputProps={{ spellCheck: false, ...(mono ? { style: { fontFamily: 'monospace' } } : {}) }}
    />
  );
}

export interface ToggleFieldProps {
  label: string;
  hint?: string;
  value: boolean;
  onChange: (value: boolean) => void;
}

export function ToggleField({ label, hint, value, onChange }: ToggleFieldProps): JSX.Element {
  return (
    <Box sx={{ my: 0.5 }}>
      <FormControlLabel
        control={<Switch checked={value} onChange={(e) => onChange(e.target.checked)} />}
        label={label}
      />
      {hint !== undefined && (
        <Typography variant="caption" color="text.secondary" display="block">
          {hint}
        </Typography>
      )}
    </Box>
  );
}

export interface SelectFieldProps<T extends string> {
  label: string;
  hint?: string;
  value: T;
  options: readonly { value: T; label: string }[];
  onChange: (value: T) => void;
}

export function SelectField<T extends string>({ label, hint, value, options, onChange }: SelectFieldProps<T>): JSX.Element {
  return (
    <MuiTextField
      select
      label={label}
      value={value}
      onChange={(e) => onChange(e.target.value as T)}
      size="small"
      fullWidth
      margin="dense"
      {...(hint !== undefined ? { helperText: hint } : {})}
    >
      {options.map((o) => (
        <MenuItem key={o.value} value={o.value}>
          {o.label}
        </MenuItem>
      ))}
    </MuiTextField>
  );
}

export interface ColorFieldProps {
  label: string;
  hint?: string;
  /** Hex color, e.g., "#1976D2". */
  value: string;
  onChange: (value: string) => void;
  /** What the renderer falls back to when empty — shown in the swatch + as placeholder. */
  defaultValue?: string;
}

export function ColorField({ label, hint, value, onChange, defaultValue }: ColorFieldProps): JSX.Element {
  const hasUserValue = value.length > 0;
  const swatchColor =
    value.length === 7 ? value : defaultValue !== undefined && defaultValue.length === 7 ? defaultValue : '#000000';
  const canReset = defaultValue !== undefined && hasUserValue;
  return (
    <Box sx={{ my: 0.75 }}>
      <Typography variant="body2" sx={{ mb: 0.5 }}>{label}</Typography>
      <Stack direction="row" spacing={1} alignItems="center">
        <Box
          component="input"
          type="color"
          value={swatchColor}
          onChange={(e) => onChange((e.target as HTMLInputElement).value.toUpperCase())}
          aria-label={`${label} swatch`}
          sx={{ width: 38, height: 38, p: 0, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'transparent', cursor: 'pointer' }}
        />
        <MuiTextField
          value={value}
          onChange={(e) => onChange(e.target.value)}
          size="small"
          fullWidth
          {...(defaultValue !== undefined ? { placeholder: defaultValue } : {})}
          inputProps={{ spellCheck: false, style: { fontFamily: 'monospace' } }}
        />
        {canReset && (
          <IconButton
            size="small"
            onClick={() => onChange('')}
            aria-label={`Reset ${label} to default (${defaultValue ?? ''})`}
            title={`Reset to default (${defaultValue ?? ''})`}
            data-testid={`color-reset-${label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <ReplayIcon fontSize="small" />
          </IconButton>
        )}
      </Stack>
      {hint !== undefined && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
          {hint}
        </Typography>
      )}
    </Box>
  );
}

export interface SubTabSpec<K extends string> {
  id: K;
  label: string;
  /** Small badge text appended to the label, e.g. "Pro" or "Ent". */
  badge?: string;
}

export interface SubTabsProps<K extends string> {
  tabs: readonly SubTabSpec<K>[];
  active: K;
  onChange: (id: K) => void;
  /** Used for screen-reader labelling of the tablist. */
  label: string;
}

export function SubTabs<K extends string>({ tabs, active, onChange, label }: SubTabsProps<K>): JSX.Element {
  return (
    <Tabs
      value={active}
      onChange={(_e, v: K) => onChange(v)}
      aria-label={label}
      variant="scrollable"
      scrollButtons="auto"
      sx={{ minHeight: 40, mb: 1, '& .MuiTab-root': { minHeight: 40, py: 0 } }}
    >
      {tabs.map((t) => (
        <Tab
          key={t.id}
          value={t.id}
          data-testid={`sub-tab-${t.id}`}
          label={
            t.badge !== undefined ? (
              <Stack direction="row" spacing={0.5} alignItems="center">
                <span>{t.label}</span>
                <Chip size="small" label={t.badge} sx={{ height: 18, fontSize: 11 }} />
              </Stack>
            ) : (
              t.label
            )
          }
        />
      ))}
    </Tabs>
  );
}
